import React, { useEffect, useState } from "react";
import { Container, Row, Col, Form, Button } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Lock, UserPlus } from "lucide-react";
import userAPI from "../../api/UserApi";
import { showError, showSuccess } from "../../utils/toastUtils";
import { setAccessToken } from "../../api/axiosInstance";
import useAlphabeticInput from "../../hooks/useAlphabeticInput";
import { handleApiValidationError } from "../../utils/apiErrorHandler";

function LoginCanvas() {
  useAlphabeticInput();
  const navigate = useNavigate();

  const INITIAL_FORM_DATA = {
    email: "",
    password: "",
  };

  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState({});

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isValidEmail = emailRegex.test(formData.email);
  const isValidPassword = formData.password.length >= 6;

  const validate = () => {
    const newErrors = {};
    if (!formData.email) {
      newErrors.email = "Please Enter Email Address";
    } else if (!isValidEmail) {
      newErrors.email = "Please enter a valid email";
    }

    if (!formData.password) {
      newErrors.password = "Enter Password";
    } else if (!isValidPassword) {
      newErrors.password = "Password must be at least 6 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    try {
      const res = await userAPI.login(formData);

      if (res.code === 200) {
        localStorage.setItem(
          "token",
          JSON.stringify({
            email: res.data.email,
            username: res.data.username,
          }),
        );

        localStorage.setItem("accessToken", res.data.accessToken);

        navigate("/users/dashboard"); // ✅ correct
      }
    } catch (err) {
      handleApiValidationError(err, setErrors);
    }
  };

  return (
    <>
      <div className="custom-loginpage-wrapper">
        <Container fluid className="h-100 p-0">
          <Row className="h-100 g-0">
            {/* Left Side: Editorial Image */}
            <Col lg={6} className="d-none d-lg-block custom-loginpage-imagebox">
              <div className="login-image-overlay"></div>
            </Col>

            {/* Right Side: Login Form */}
            <Col
              lg={6}
              className="d-flex justify-content-center align-items-center p-4 p-md-5"
            >
              <div className="custom-loginpage-formbox">
                <h1 className="custom-loginpage-title mb-1">Login</h1>
                <p className="text-muted mb-5">
                  Enter your details to access your account
                </p>

                <Form method="post" onSubmit={handleSubmit}>
                  <Form.Group className="mb-3" controlId="email">
                    <Form.Label className="custom-form-label">
                      Email Address <span className="text-danger">*</span>
                    </Form.Label>
                    <div className="input-group-custom">
                      <Mail size={18} className="input-icon" />
                      <Form.Control
                        type="email"
                        name="email"
                        placeholder="Email Address"
                        value={formData.email}
                        onChange={handleChange}
                      />
                    </div>
                    {errors && errors.email && (
                      <span className="text-danger">{errors.email}</span>
                    )}
                  </Form.Group>

                  <Form.Group className="mb-3" controlId="password">
                    <Form.Label className="custom-form-label">
                      Password <span className="text-danger">*</span>
                    </Form.Label>
                    <div className="input-group-custom">
                      <Lock size={18} className="input-icon" />
                      <Form.Control
                        type="password"
                        name="password"
                        placeholder="Password"
                        value={formData.password}
                        onChange={handleChange}
                      />
                    </div>
                    {errors && errors.password && (
                      <span className="text-danger">{errors.password}</span>
                    )}
                  </Form.Group>

                  <Button
                    className="custom-loginpage-submitbtn btn w-100 py-3"
                    type="submit"
                  >
                    LOGIN
                  </Button>

                  <div className="text-center mt-3 pt-1 border-top">
                    <p className="small text-muted">
                      Don't have an account?
                      <Link
                        to="/users/register"
                        className="text-dark fw-bold ms-2"
                      >
                        Register
                      </Link>
                    </p>
                  </div>
                </Form>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
}

export default LoginCanvas;
